import * as React from 'react';

import { Button, Icon } from '@bdl-cmn-shared-packages-npm/design-system';
import {
	ComponentFields,
	ComponentParams,
	ComponentRendering,
	Text,
	getFieldValue,
} from '@sitecore-jss/sitecore-jss-react';
import { breakpoint, breakpointMax } from '../../style';
import { css, jsx, keyframes } from '@emotion/react';

import { SitecoreGenericTextField } from '../../interfaces';
import hexaGrey from './hexaGrey.svg';
import hexaLightGreen from './hexaLightGreen.svg';
import hexaLightRed from './hexaLightRed.svg';
import hexaMint from './hexaMint.svg';
import logo from './luxXPrime.svg';
import { renderLink } from '../../utils/helper';
import { style } from './index.style';
import DictionaryComponent from '../../core/components/Dictionary/DictionaryComponent';

/** @jsx jsx */

export interface LuxXPrimeDoubleContentProps {
	fields: any;
	params?: ComponentParams;
	rendering: ComponentRendering;
}

const RenderData = (title: any, description: any) => (
	<div css={style.bloc}>
		<div css={style.icon}>
			<img src={renderLink(hexaGrey)} css={style.hexa} alt={'image'} />
			<div css={style.hoverIcon}>
				<Icon icon='check' />
			</div>
		</div>
		<div css={style.title}>
			<Text field={title as SitecoreGenericTextField} />
		</div>
		<div css={style.description}>
			<Text field={description as SitecoreGenericTextField} />
		</div>
	</div>
);

export const LuxXPrimeDoubleContent: React.FunctionComponent<LuxXPrimeDoubleContentProps> =
	(props: LuxXPrimeDoubleContentProps): React.ReactElement => {
		const { fields, params, rendering } = props;

		const logo: any = getFieldValue<any>(fields, 'logo', null);
		const btnLabel: string = getFieldValue<string>(fields, 'btnLabel', '');
		const btnLink: string = getFieldValue<string>(fields, 'btnLink', '');
		const btnIcon: string = getFieldValue<string>(
			fields,
			'btnIcon',
			'chevron-right'
		);
		const icon1: string = getFieldValue<string>(fields, 'bloc1icon', '');
		const icon2: string = getFieldValue<string>(fields, 'bloc2icon', '');
		const icon3: string = getFieldValue<string>(fields, 'bloc3icon', '');
		const icon4: string = getFieldValue<string>(fields, 'bloc4icon', '');
		const noLogo: boolean = getFieldValue<boolean>(fields, 'noLogo', true);
		const upperLogo: boolean = getFieldValue<boolean>(fields, 'upperLogo', false);

		const iconColor = getFieldValue<any>(fields, 'iconColor', null);
		const backgroundColor = getFieldValue<any>(fields, "backgroundColor", null);

		const hoverIcon = css`
			position: absolute;
			color: ${iconColor === 'lightGreen'
				? '#008C46'
				: iconColor === 'mint'
				? 'var(--mint-600, #00B86B)'
				: '#CB1234'};
		`;

		const getIconColor = () => {
			if (iconColor === 'lightGreen') {
				return hexaLightGreen;
			}
			if (iconColor === 'lightRed') {
				return hexaLightRed;
			}
			if (iconColor === 'mint') {
				return hexaMint;
			} else {
				return hexaGrey;
			}
		};

		const getBackgroundColor = () => {
			if (backgroundColor === "apple100") {
				return `linear-gradient(82deg, rgba(34, 170, 95, 0.20) 0%, rgba(199, 214, 77, 0.10) 53.76%)`;
			}
			if (backgroundColor === "apple500") {
				return `linear-gradient(82deg, #166F3E 0.08%, #018C45 31.62%, #C7D64D 99.36%)`;
			}
			if (backgroundColor === "gradientGreen") {
				return `linear-gradient(83deg, #166F3E 1.21%, rgba(46, 157, 71, 0.94) 41.77%, rgba(199, 214, 77, 0.75) 65.16%, rgba(199, 214, 77, 0.00) 90.12%)`;
			}
			if (backgroundColor === "peach100") {
				return `linear-gradient(263deg, rgba(248, 159, 121, 0.00) 0%, rgba(248, 159, 121, 0.60) 100%)`;
			}
			if (backgroundColor === "peach500") {
				return `linear-gradient(82deg, #A21228 0.08%, #CB2035 31.62%, #F89F79 99.36%)`;
			}
			if (backgroundColor === "gradientPeach") {
				return `linear-gradient(83deg, rgba(248, 159, 121, 0.60) 0.17%, rgba(248, 159, 121, 0.00) 100%)`;
			}
			if (backgroundColor === "gradientGrey") {
				return `linear-gradient(90deg, #253645 11.63%, rgba(37, 54, 69, 0) 85.53%)`;
			}
			if (backgroundColor === "saphirMint") {
				return `linear-gradient(90.59deg, #263099 0.61%, #007A99 42.06%, #65EBB3 131.06%)`;
			}
			if (backgroundColor === "white") {
				return `#ffffff`;
			}
			else {
				return "#ffffff";
			}
		};

		const container = css`
			width: 100%;
			gap: 40px;
			max-width: 1280px;
			padding-left: 40px;
			padding-right: 40px;
			margin-left: auto;
			margin-right: auto;
			margin-bottom: 96px;
			margin-top: 96px;
			display: flex;
			background: ${getBackgroundColor()};
			@media (max-width: ${breakpointMax}) {
			  flex-direction: column;
			  gap: 0;
			  display: flex;
			}
		`;

		const action = css`
    		margin-top: 32px;
			margin-bottom: 32px;
			text-align: -webkit-center;
  		`;

		const leftTextStyle = css`
			text-align: right;
		`;

		const containerWithGap = css`
			display: flex;
			gap: 120px; 
			@media (max-width: ${breakpointMax}) {
				gap: 0;
			}
		`;

		return (
			<div css={[container, containerWithGap]}>
				<div css={style.left}>
					{upperLogo && !noLogo && logo && logo.src && (
						<div
							css={css`
								order: -1;
								margin-bottom: 20px;
								text-align: right;
							`}
						>
							<img src={renderLink(logo && logo.src)} alt={'image'} css={css`max-width: 100%;height: auto;display: block; min-width: 50px; min-height: 50px;`} />
						</div>
					)}
					<div css={[style.top, leftTextStyle]}>
						<Text field={fields && (fields.title as SitecoreGenericTextField)} />
					</div>
					{!upperLogo && !noLogo && logo && logo.src && (
						<div css={[style.bottom, leftTextStyle]}>
							<img src={renderLink(logo && logo.src)} alt={'image'} css={css`max-width: 100%;height: auto; min-width: 50px; min-height: 50px;`}/>
						</div>
					)}
				</div>
				<div css={style.right}>
					<div css={style.content}>
						<div css={style.bloc}>
							<div css={style.icon}>
								<img src={getIconColor()} css={style.hexa} alt={'image'} />
								<div css={hoverIcon}>
									<Icon icon={icon1} />
								</div>
							</div>
							<div css={style.title}>
								<Text
									field={fields && (fields.bloc1Title as SitecoreGenericTextField)}
								/>
							</div>
							<div css={style.description}>
								<DictionaryComponent text={fields.bloc1description.value} />
							</div>
						</div>
						<div css={style.bloc}>
							<div css={style.icon}>
								<img src={getIconColor()} css={style.hexa} alt={'image'} />
								<div css={hoverIcon}>
									<Icon icon={icon2} />
								</div>
							</div>
							<div css={style.title}>
								<Text
									field={fields && (fields.bloc2Title as SitecoreGenericTextField)}
								/>
							</div>
							<div css={style.description}>
								<DictionaryComponent text={fields.bloc2description.value} />
							</div>
						</div>
						<div css={style.bloc}>
							<div css={style.icon}>
								<img src={getIconColor()} css={style.hexa} alt={'image'} />
								<div css={hoverIcon}>
									<Icon icon={icon3} />
								</div>
							</div>
							<div css={style.title}>
								<Text
									field={fields && (fields.bloc3Title as SitecoreGenericTextField)}
								/>
							</div>
							<div css={style.description}>
								<DictionaryComponent text={fields.bloc3description.value} />
							</div>
						</div>
						<div css={style.bloc}>
							<div css={style.icon}>
								<img src={getIconColor()} css={style.hexa} alt={'image'} />
								<div css={hoverIcon}>
									<Icon icon={icon4} />
								</div>
							</div>
							<div css={style.title}>
								<Text
									field={fields && (fields.bloc4Title as SitecoreGenericTextField)}
								/>
							</div>
							<div css={style.description}>
								<DictionaryComponent text={fields.bloc4description.value} />
							</div>
						</div>
					</div>
					{btnLabel && (
						<div css={action}>
							<Button
								label={btnLabel}
								onClick={() =>
									btnLink ? (window.location.href = btnLink) : console.log
								}
								iconRight={btnIcon as any}
							/>
						</div>
					)}
				</div>
			</div>
		);
	};
